import { Email, Lock, Logout, Person, Phone, Save, Visibility, VisibilityOff } from "@mui/icons-material";
import { Alert, Avatar, Box, Button, Card, CardContent, Chip, Divider, Grid, IconButton, Stack, TextField, Typography } from "@mui/material";
import { createEmptyInstructor, Instructor } from "../../../models/instructor";
import { memo, useEffect, useState } from "react";
import { useDomainActions } from "../../../hooks/useDomainActions";
import BackdropLoader from "../../../components/backdrop-loader";

function Profile () {
    const { instructor, getInstructor, updateInstructor, logOutInstructor } = useDomainActions();

    const [locInstructor, setLocInstructor] = useState<Instructor>(createEmptyInstructor());

    const [showPassword, setShowPassword] = useState(false);
    const [showNewPassword, setShowNewPassword] = useState(false);

    
    const [loading, setLoading] = useState(false);

    const handleInstructorChange = <K extends keyof Instructor>(field: K, value: Instructor[K]) => {
        setLocInstructor((prev) => ({
            ...prev,
            [field]: value,
        }));
    };

    const isPasswordValid = (password: string) => {
      return /^(?=.*[A-Za-z])(?=.*\d)[A-Za-z\d]{8,}$/.test(password);
    };

    const _updateInstructor = async (action: number) => {
      setLoading(true);
      await updateInstructor(locInstructor, action);
      setLoading(false);
    };

    const handleLogout = async () => {
      await logOutInstructor();
    };

    useEffect(() => {
      if (instructor) {
        setLocInstructor(instructor);   
      }
    }, [instructor]);

    useEffect(() => {
        const getInstructorFromStore = async () => {
            await getInstructor();
        };

        if (!instructor) {
            getInstructorFromStore();
        }
    }, []);

    return (
        <Stack spacing={3}>
          {!instructor && <BackdropLoader/>}
           <Typography
                variant="subtitle1"
                sx={{
                  fontWeight: 600,
                  color: 'primary.main',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  mb: 2
                }}
              >
                <Person sx={{ color: 'primary.main' }}/>
                Basic Details
              </Typography>
              <Divider sx={{ mb: 3 }} />
              <Grid container spacing={2}>
                <Grid size={{xs: 12, md: 6}}>
                  <TextField
                    fullWidth
                    label="Full Name"
                    variant="outlined"
                    value={locInstructor.name}
                    onChange={(e) => handleInstructorChange("name", e.target.value)}
                    sx={{
                      '& .MuiInputBase-root': {
                        backgroundColor: 'white'
                      }
                    }}
                  />
                </Grid>
                <Grid size={{xs: 12, md: 6}}>
                  <TextField
                    fullWidth
                    label="Email Address"
                    variant="outlined"
                    type="email"
                    value={locInstructor.email}
                    disabled
                    sx={{
                      '& .MuiInputBase-root': {
                        backgroundColor: 'white'
                      }
                    }}
                  />
                </Grid>
                <Grid size={{xs: 12, md: 6}}>
                  <TextField
                    fullWidth
                    label="Phone Number"
                    variant="outlined"
                    type="tel"
                    value={locInstructor.phone_number}
                    onChange={(e) => handleInstructorChange("phone_number", e.target.value)}
                    sx={{
                      '& .MuiInputBase-root': {
                        backgroundColor: 'white'
                      }
                    }}
                  />
                </Grid>
              </Grid>
              <Box sx={{ mt: 3, display: "flex", justifyContent: "flex-end" }}>
                <Button
                  variant="contained"
                  startIcon={<Save />}
                  onClick={() => _updateInstructor(0)}
                  disabled={loading || !locInstructor.name || !locInstructor.phone_number}
                  sx={{
                    px: 3,
                    py: 1.5,
                    borderRadius: 2,
                    textTransform: 'none',
                    fontWeight: 600,
                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                    '&:hover': {
                      boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                      transform: 'translateY(-1px)'
                    },
                    '&:active': {
                      transform: 'translateY(0)'
                    },
                    transition: 'all 0.2s ease-in-out'
                  }}
                >
                  Save Changes
                </Button>
              </Box>
          
          <Typography
                variant="subtitle1"
                sx={{
                  fontWeight: 600,
                  color: 'primary.main',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  mb: 2
                }}
              >
                <Lock sx={{ color: 'primary.main' }}/>
                Change Password
              </Typography>
              <Divider sx={{ mb: 3 }} />
              <Alert severity="info" sx={{ mb: 3 }}>
                Ensure your password is at least 8 characters long and contains a mix of letters and numbers.
              </Alert>
              <Grid container spacing={2}>
                <Grid size={{xs: 12, md: 6}}>
                  <TextField
                    fullWidth
                    label="Current Password"
                    variant="outlined"
                    type={showPassword ? "text" : "password"}
                    value={locInstructor.password}
                    onChange={(e) => handleInstructorChange("password", e.target.value)}
                    sx={{
                      '& .MuiInputBase-root': {
                        backgroundColor: 'white'
                      }
                    }}
                    InputProps={{
                      endAdornment: (
                        <IconButton
                          onClick={() => setShowPassword(!showPassword)}
                          edge="end"
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      ),
                    }}
                  />
                </Grid>
                <Grid size={{xs: 12, md: 6}}>
                  <TextField
                    fullWidth
                    label="New Password"
                    variant="outlined"
                    type={showNewPassword ? "text" : "password"}
                    value={locInstructor.password_repeat}
                    onChange={(e) => handleInstructorChange("password_repeat", e.target.value)}
                    error={locInstructor.password_repeat.length > 0 && !isPasswordValid(locInstructor.password_repeat)}
                    helperText={
                      locInstructor.password_repeat.length > 0 && !isPasswordValid(locInstructor.password_repeat)
                        ? "Password must be at least 8 characters long and contain letters and numbers."
                        : ""
                    }
                    sx={{
                      '& .MuiInputBase-root': {
                        backgroundColor: 'white'
                      }
                    }}
                    InputProps={{
                      endAdornment: (
                        <IconButton
                          onClick={() => setShowNewPassword(!showNewPassword)}
                          edge="end"
                        >
                          {showNewPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      ),
                    }}
                  />
                </Grid>
              </Grid>
              <Box sx={{ mt: 3, display: "flex", justifyContent: "flex-end" }}>
                <Button
                  variant="contained"
                  startIcon={<Lock />}
                  onClick={() => _updateInstructor(1)}
                  disabled={loading || !isPasswordValid(locInstructor.password_repeat)}
                  sx={{
                    px: 3,
                    py: 1.5,
                    borderRadius: 2,
                    textTransform: 'none',
                    fontWeight: 600,
                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                    '&:hover': {
                      boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                      transform: 'translateY(-1px)'
                    },
                    '&:active': {
                      transform: 'translateY(0)'
                    },
                    transition: 'all 0.2s ease-in-out'
                  }}
                >
                  Update Password
                </Button>
              </Box>

          <Button
            variant="outlined"
            color="primary"
            startIcon={<Logout />}
            onClick={handleLogout}
            sx={{
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 600,
              padding: '8px 16px',
              fontSize: '0.875rem',
              border: '1.5px solid',
              borderColor: 'error.main',
              color: 'error.main',
              '&:hover': {
                  backgroundColor: 'error.main',
                  color: 'white',
                  borderColor: 'error.main',
                  transform: 'translateY(-1px)',
                  boxShadow: '0 4px 12px rgba(239, 68, 68, 0.3)'
              },
              '&:active': {
                  transform: 'translateY(0)',
              },
              transition: 'all 0.2s ease'
          }}
          >
            Logout
          </Button>
        </Stack>
    )
}

export default memo(Profile);